import type {
  IActiveTournamentsResponse,
  IGetTournamentRewardsResponse,
  ITournamentData,
  ITournamentDetailedData,
  ITournamentLeaderboardItem,
  ITournamentLeaderboardResponse,
  ITournamentPrizeData,
  ITournamentRewardData,
  TTournamentStatus,
} from '#shared/types/tournaments.types';
import { LazyModalTournamentWinner } from '#components';
import { formatBalance } from '#shared/formatters/currency';
import { formatMsToLocalTime } from '#shared/formatters/time';
import { ETournamentEvents, ETournamentStatus, ETournamentType } from '#shared/types/tournaments.types';

const TOURNAMENT_DUMMY: Partial<ITournamentData> = {
  tournamentId: 'default',
  tournamentName: 'Default Tournament',
  tournamentType: ETournamentType.Weekly,
  status: ETournamentStatus.Ended,
  gameName: '',
  gameId: 0,
  isParticipating: false,
  prize: 0,
  timeLeft: 0,
  rewards: [],
};

function prepareTournamentData(list: ITournamentDetailedData[]) {
  return list
    .map((tournament) => {
      const { id, prize, tournamentName, tournamentType, gameName, gameId, isParticipating, rewards, winners } =
        tournament;
      const tillEnd = Math.max(0, Math.round(tournament.tillEnd));
      const tillStart = Math.max(0, Math.round(tournament.tillStart));

      const startsAtString = formatMsToLocalTime(tournament.startAt);
      const endsAtString = formatMsToLocalTime(tournament.endAt);

      let status: TTournamentStatus = ETournamentStatus.Ended;
      let timeLeft = 0;

      if (tillStart > 0) {
        status = ETournamentStatus.Upcoming;
        timeLeft = tillStart * 1000;
      } else if (tillStart === 0 && tillEnd > 0) {
        status = ETournamentStatus.Active;
        timeLeft = tillEnd * 1000;
      }

      return {
        tournamentId: id,
        tournamentName,
        tournamentType,
        gameName,
        gameId,
        isParticipating,
        timeLeft,
        startsAtString,
        endsAtString,
        status,
        rewards,
        winners: winners.map((winner: any, index: any) => ({
          key: `${winner.index + 1}-${winner.userDisplayName}`,
          username: winner.userDisplayName || 'Unknown',
          rank: index + 1,
          prize: `$${formatBalance(rewards[index] || 0)}`,
        })),
        prize: `$${formatBalance(prize)}`,
      };
    })
    .sort((a, b) => {
      if (a.status === ETournamentStatus.Active) return -1;
      if (b.status === ETournamentStatus.Active) return 1;
      if (a.status === ETournamentStatus.Upcoming) return -1;
      if (b.status === ETournamentStatus.Upcoming) return 1;
      return 0;
    }) as unknown as ITournamentData[];
}

export const useTournamentsStore = defineStore('tournaments', () => {
  const { $authApi, callHook } = useTypedNuxtApp();
  const { tournamentsEnabled } = useRuntimeConfig().public;

  const isEnabled = ref<boolean>(tournamentsEnabled);
  const list = ref<ITournamentData[]>([]);
  const prizeConfig: Readonly<ITournamentPrizeData[]> = [
    {
      rank: '1st',
      prize: '$15.00',
    },
    {
      rank: '2nd',
      prize: '$12.00',
    },
    {
      rank: '3rd',
      prize: '$9.00',
    },
    {
      rank: '4th',
      prize: '$7.00',
    },
    {
      rank: '5th',
      prize: '$6.00',
    },
    {
      rank: '6th',
      prize: '$5.50',
    },
    {
      rank: '7th',
      prize: '$5.00',
    },
    {
      rank: '8th',
      prize: '$4.50',
    },
    {
      rank: '9th',
      prize: '$4.00',
    },
    {
      rank: '10th',
      prize: '$3.50',
    },
    {
      rank: '11th',
      prize: '$3.00',
    },
    {
      rank: '12th',
      prize: '$2.50',
    },
    {
      rank: '13th',
      prize: '$2.25',
    },
    {
      rank: '14th',
      prize: '$2.00',
    },
    {
      rank: '15th',
      prize: '$1.75',
    },
    {
      rank: '16th',
      prize: '$1.50',
    },
    {
      rank: '17th',
      prize: '$1.25',
    },
    {
      rank: '18th',
      prize: '$1.00',
    },
    {
      rank: '19th',
      prize: '$0.75',
    },
    {
      rank: '20th',
      prize: '$0.50',
    },
  ];

  const requestIsPending = ref(false);
  const tournamentWinnerData = ref({});
  const userRewards = ref<ITournamentRewardData[]>([]);

  let tickTimerId: NodeJS.Timeout | undefined;

  /**
   * Finds a tournament by its ID and returns it, or returns a fake object if not found.
   */
  function getTournamentById(tournamentId?: string) {
    return list.value.find((tournament) => tournament.tournamentId === tournamentId) || TOURNAMENT_DUMMY;
  }

  /**
   * Requests a list of active tournaments from the server and updates the local state with the received data.
   */
  async function getAvailableTournaments() {
    if (requestIsPending.value) return;

    try {
      requestIsPending.value = true;

      const sessionKey = await stores.user.getIdToken();

      const { data, hasError } = await $authApi.post<IActiveTournamentsResponse>('/tournament/getActiveTournaments', {
        sessionKey,
      });

      if (hasError || !data) {
        throw new Error('Failed to get tournaments data');
      }

      list.value = prepareTournamentData(data);
      isEnabled.value = list.value?.length > 0;
    } catch (error: any) {
      console.error(error?.response?.data || error.message);
    } finally {
      requestIsPending.value = false;
    }
  }

  /**
   * Fetches the leaderboard of a specified tournament.
   */
  async function getTournamentLeaderboard(tournamentId: string) {
    let leaderboard: ITournamentLeaderboardItem[] = [];

    try {
      const sessionKey = await stores.user.getIdToken();
      const data = await $authApi.post<ITournamentLeaderboardResponse>('/tournament/leaderBoard', {
        sessionKey,
        tournamentId,
      });

      if (data.hasError || !data.data) {
        throw new Error('Failed to get optInTournament data');
      }

      leaderboard = data.data;
    } catch (error: any) {
      console.error(error?.response?.data || error.message);
    }

    return leaderboard;
  }

  /**
   * Attempts to opt in a user to a specified tournament by sending a request to the server.
   */
  async function optInTournament(tournamentId: string) {
    if (requestIsPending.value) return;

    let isSuccessful = false;

    try {
      requestIsPending.value = true;

      const sessionKey = await stores.user.getIdToken();
      const { hasError } = await $authApi.post<{ hasError: boolean }>('/tournament/optIn', {
        sessionKey,
        tournamentId,
      });

      if (hasError) {
        throw new Error('Failed to opt in to the tournament');
      }

      isSuccessful = true;
    } catch (error: any) {
      console.error(error?.response?.data || error.message);
    } finally {
      requestIsPending.value = false;
    }

    return isSuccessful;
  }

  /**
   * Checks if there are available rewards for the user and displays a popup if so.
   * If there are multiple rewards, it will only show the first one and shift the array
   * after the user collects the first one, so the next reward will be shown.
   */
  async function getUserAvailableReward() {
    if (userRewards.value.length) {
      const reward = shiftUserReward();
      if (reward) await showTournamentWinnerPopup(reward);
      return;
    }

    try {
      const sessionKey = await stores.user.getIdToken();
      const { hasError, data } = await $authApi.post<IGetTournamentRewardsResponse>('/tournament/getRewards', {
        sessionKey,
      });

      if (hasError || !data) {
        throw new Error('Failed to get tournaments rewards');
      }

      userRewards.value = data || [];

      const reward = shiftUserReward();
      if (reward) await showTournamentWinnerPopup(reward);
    } catch (error: any) {
      console.error(error?.response?.data || error.message);
    }
  }

  /**
   * Removes the first reward from the user's reward list and returns it.
   * This method is used to show one reward at a time to the user.
   */
  function shiftUserReward(): ITournamentRewardData | undefined {
    const clone = [...userRewards.value];
    const reward = clone.shift();
    userRewards.value = clone;
    return reward;
  }

  async function showTournamentWinnerPopup(reward: ITournamentRewardData) {
    await stores.modals.open('tournament-winner', LazyModalTournamentWinner, { reward });
  }

  /**
   * Starts a recurring countdown timer that emits a {@link ETournamentEvents.CountdownTick} every second.
   * The timer is stopped and restarted on each call to prevent multiple timers from running simultaneously.
   */
  async function scheduleCountdownEmitter() {
    clearTimeout(tickTimerId);
    await callHook(ETournamentEvents.CountdownTick);
    tickTimerId = setTimeout(scheduleCountdownEmitter, 1000);
  }

  /**
   * Stops the recurring countdown timer and clears any scheduled timer events.
   *
   * This function is idempotent; it will not throw an error if the timer is already stopped.
   */
  function stopCountdownEmitter() {
    clearTimeout(tickTimerId);
    tickTimerId = undefined;
  }

  return {
    isEnabled,
    list,
    prizeConfig,
    requestIsPending,
    tournamentWinnerData,
    userRewards,
    tickTimerId,

    getAvailableTournaments,
    getUserAvailableReward,
    getTournamentById,
    getTournamentLeaderboard,
    scheduleCountdownEmitter,
    stopCountdownEmitter,
    optInTournament,
  };
});
